import { useQuery } from '@tanstack/react-query';
import type {
  Espace,
  EspaceCreation,
  EspaceMiseAJour,
  Mur,
  PlanAccrochage,
  PlanAccrochageCreation,
  PlanAccrochageMiseAJour,
  AlerteAccrochage,
} from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier } from './cles.js';
import { estApiError } from './errors.js';

export type EspacePresente = Pick<
  Espace,
  'nom' | 'nature' | 'lieuId' | 'murs' | 'archive' | 'revision'
> & { id: string };
export type EspaceResume = Omit<EspacePresente, 'murs'> & { murs: Omit<Mur, 'zones'>[] };
export type PlanPresente = Pick<
  PlanAccrochage,
  'nom' | 'expositionId' | 'espaceId' | 'axeCm' | 'etat' | 'placements' | 'revision'
> & { id: string; appliqueLe: string | null; appliquePar: string | null };
export interface OeuvreAccrochage {
  id: string;
  titre: string;
  artisteId: string;
  artiste: string;
  dimensions: { largeur: number; hauteur: number } | null;
  statut: string;
  archive: boolean;
  apercuUrl: string | null;
}
export type AlertePlan =
  | (AlerteAccrochage & { murId: string })
  | { nature: 'hors_exposition' | 'oeuvre_retiree'; oeuvreId: string };
export interface DetailPlan {
  plan: PlanPresente;
  espace: EspacePresente;
  oeuvres: OeuvreAccrochage[];
  alertes: AlertePlan[];
}
export interface RevisionPerimee {
  revision: number;
  modifiePar: string | null;
  modifieLe: string | null;
}
export function lireRevisionPerimee(erreur: unknown): RevisionPerimee | null {
  if (!estApiError(erreur) || erreur.status !== 409) return null;
  const details = erreur.details as (Partial<RevisionPerimee> & { raison?: string }) | null;
  if (details?.raison !== 'revision_perimee' || typeof details.revision !== 'number') return null;
  return {
    revision: details.revision,
    modifiePar: details.modifiePar ?? null,
    modifieLe: details.modifieLe ?? null,
  };
}
export type NatureDocumentAccrochage =
  'feuille-accrochage' | 'plan-ensemble' | 'cartels' | 'pointage';
const base = (galerie: string) => `/g/${encodeURIComponent(galerie)}`;
export const accrochageApi = {
  espaces: (galerie: string, archive = false, appel?: AppelApi) =>
    api.get<{ espaces: EspaceResume[] }>(`${base(galerie)}/espaces?archive=${String(archive)}`, {
      galerie,
      ...appel,
    }),
  espace: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<{ espace: EspacePresente }>(`${base(galerie)}/espaces/${encodeURIComponent(id)}`, {
      galerie,
      ...appel,
    }),
  creerEspace: (galerie: string, corps: EspaceCreation, appel?: AppelApi) =>
    api.post<{ espace: EspacePresente }>(`${base(galerie)}/espaces`, corps, { galerie, ...appel }),
  enregistrerEspace: (galerie: string, id: string, corps: EspaceMiseAJour, appel?: AppelApi) =>
    api.put<{ espace: EspacePresente }>(
      `${base(galerie)}/espaces/${encodeURIComponent(id)}`,
      corps,
      { galerie, ...appel },
    ),
  dupliquerEspace: (galerie: string, id: string, revision: number, appel?: AppelApi) =>
    api.post<{ espace: EspacePresente }>(
      `${base(galerie)}/espaces/${encodeURIComponent(id)}/dupliquer`,
      { revision },
      { galerie, ...appel },
    ),
  archiverEspace: (
    galerie: string,
    id: string,
    revision: number,
    archive: boolean,
    appel?: AppelApi,
  ) =>
    api.post<{ espace: EspacePresente }>(
      `${base(galerie)}/espaces/${encodeURIComponent(id)}/${archive ? 'archiver' : 'restaurer'}`,
      { revision },
      { galerie, ...appel },
    ),
  plans: (galerie: string, expositionId?: string, appel?: AppelApi) =>
    api.get<{ plans: PlanPresente[] }>(
      `${base(galerie)}/plans-accrochage${expositionId ? `?expositionId=${encodeURIComponent(expositionId)}` : ''}`,
      { galerie, ...appel },
    ),
  plan: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<DetailPlan>(`${base(galerie)}/plans-accrochage/${encodeURIComponent(id)}`, {
      galerie,
      ...appel,
    }),
  creerPlan: (galerie: string, corps: PlanAccrochageCreation, appel?: AppelApi) =>
    api.post<{ plan: PlanPresente }>(`${base(galerie)}/plans-accrochage`, corps, {
      galerie,
      ...appel,
    }),
  enregistrerPlan: (
    galerie: string,
    id: string,
    corps: PlanAccrochageMiseAJour,
    appel?: AppelApi,
  ) =>
    api.put<{ plan: PlanPresente }>(
      `${base(galerie)}/plans-accrochage/${encodeURIComponent(id)}`,
      corps,
      { galerie, ...appel },
    ),
  dupliquerPlan: (galerie: string, id: string, revision: number, appel?: AppelApi) =>
    api.post<{ plan: PlanPresente }>(
      `${base(galerie)}/plans-accrochage/${encodeURIComponent(id)}/dupliquer`,
      { revision },
      { galerie, ...appel },
    ),
  appliquer: (galerie: string, id: string, revision: number, appel?: AppelApi) =>
    api.post<{ plan: PlanPresente; oeuvres: string[] }>(
      `${base(galerie)}/plans-accrochage/${encodeURIComponent(id)}/appliquer`,
      { revision, confirmer: true },
      { galerie, ...appel },
    ),
  document: (
    galerie: string,
    id: string,
    nature: NatureDocumentAccrochage,
    langue: 'fr' | 'en',
    murId?: string,
    appel?: AppelApi,
  ) =>
    api.get<{ url: string; expireLe: string; expireDansSecondes: number; nomFichier: string }>(
      `${base(galerie)}/plans-accrochage/${encodeURIComponent(id)}/documents/${nature}.pdf?${new URLSearchParams({ langue, ...(murId ? { murId } : {}) })}`,
      { galerie, ...appel },
    ),
};
export function useEspaces(galerie: string, archive = false) {
  return useQuery({
    queryKey: cleMetier(galerie, 'accrochage', 'espaces', archive),
    queryFn: (contexte) => accrochageApi.espaces(galerie, archive, appelDeRequete(contexte)),
  });
}
export function useEspace(galerie: string, id: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'accrochage', 'espace', id),
    queryFn: (contexte) => accrochageApi.espace(galerie, id, appelDeRequete(contexte)),
  });
}
export function usePlans(galerie: string, expositionId?: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'accrochage', 'plans', expositionId),
    queryFn: (contexte) => accrochageApi.plans(galerie, expositionId, appelDeRequete(contexte)),
  });
}
export function usePlan(galerie: string, id: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'accrochage', 'plan', id),
    queryFn: (contexte) => accrochageApi.plan(galerie, id, appelDeRequete(contexte)),
  });
}
